import React from "react";
import { StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";
import { colors, layout } from "@/constants";
import { Pattern } from "@/store/types";
import Card from "./base/Card";
import Text from "./base/Text";
import Icon, { IconName } from "./base/Icon";
import Interactive from "./base/Interactive";
import SignificanceIndicator from "./SignificanceIndicator";
import Accordion from "./base/Accordion";

/**
 * Props for the PatternCard component
 */
interface PatternCardProps {
  /** The pattern data to display */
  pattern: Pattern;
}

/**
 * Gets the icon name based on pattern category
 * @param category - The pattern category
 * @returns Material icon name
 */
const getCategoryIcon = (category: "asset" | "obstacle"): IconName => {
  return category === "asset" ? "fitness_center" : "support";
};

/**
 * Card component for displaying a pattern
 * Shows name, category icon, significance, description, and expandable evidence
 *
 * @param props - The component props
 * @returns A styled pattern card
 */
export default function PatternCard({ pattern }: PatternCardProps) {
  const router = useRouter();
  const iconName = getCategoryIcon(pattern.category);

  const handleExplore = () => {
    router.push({ pathname: "/chat", params: { pattern: pattern.name } });
  };

  return (
    <Card backgroundColor={colors.aiContainer}>
      <View style={styles.container}>
        {/* Header: Icon + Name */}
        <View style={styles.header}>
          <Icon name={iconName} variant="medium" style={styles.headerAccent} />
          <Text variant="medium" style={[styles.name, styles.headerAccent]}>
            {pattern.name}
          </Text>
        </View>

        {/* Significance Indicator */}
        <SignificanceIndicator significance={pattern.significance} />

        {/* Description */}
        <Text>{pattern.description}</Text>

        {/* Evidence Accordion */}
        <Accordion
          headerChildren={
            <>
              <Icon
                name="question_mark"
                size="small"
                color="secondary"
                style={{ marginLeft: -2 }}
              />
              <Text color="secondary" size="small">
                Evidence across sessions
              </Text>
            </>
          }
          contentChildren={<Text>{pattern.summaryOfEvidence}</Text>}
          style={styles.accordion}
        />

        {/* Explore link: starts a voice session focused on this pattern */}
        <Interactive
          onPress={handleExplore}
          accessibilityLabel={`Explore ${pattern.name} in a conversation`}
          style={styles.exploreLink}
        >
          <Icon name="graphic_eq" size="small" style={styles.exploreAccent} />
          <Text variant="medium" size="small" style={styles.exploreAccent}>
            Explore this pattern
          </Text>
        </Interactive>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: layout.space.md
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.sm
  },
  name: {
    flex: 1
  },
  headerAccent: {
    color: colors.onAiContainer
  },
  accordion: {
    backgroundColor: colors.aiContainerHigh,
    marginHorizontal: 0,
    marginVertical: 0,
    padding: 0,
    shadowOpacity: 0,
    elevation: 0
  },
  exploreLink: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: layout.space.sm,
    paddingVertical: layout.space.xs,
    borderRadius: layout.borderRadius.small
  },
  exploreAccent: {
    color: colors.onAiContainer
  }
});
